"use client";

import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import { memo, useCallback } from "react";
import { trackArtistDiscovery } from "@/lib/services/analytics";
import { ArtistPhoto, type ArtistPhotoProps } from "./artist-photo";

export interface ClickableArtistPhotoProps extends ArtistPhotoProps {
	href: string | null;
	onClick?: (e: React.MouseEvent) => void;
	trackingData?: {
		artistId: string;
		artistName: string;
		source: "festival" | "search" | "discover" | "profile";
	};
	ariaLabel?: string;
}

/**
 * Clickable wrapper for ArtistPhoto that provides consistent hover effects
 * and navigation behavior across the application.
 *
 * Features:
 * - Standardized hover effects (scale, ring)
 * - Active state feedback
 * - Keyboard navigation support
 * - Analytics tracking
 * - Accessibility features
 */
export const ClickableArtistPhoto = memo<ClickableArtistPhotoProps>(
	function ClickableArtistPhoto({
		href,
		onClick,
		trackingData,
		ariaLabel,
		className,
		// Pass through all ArtistPhoto props
		name,
		imageUrl,
		size = "md",
		priority = false,
		rounded = "circle",
		aspectRatio = "square",
		isSurprise = false,
		isProgram = false,
	}) {
		const handleClick = useCallback(
			(e: React.MouseEvent) => {
				e.stopPropagation(); // Prevent parent click handlers

				// Track artist discovery if data provided
				if (trackingData) {
					trackArtistDiscovery(
						trackingData.artistId,
						trackingData.artistName,
						trackingData.source,
						"viewed",
					);
				}

				// Call custom onClick if provided
				onClick?.(e);
			},
			[onClick, trackingData],
		);

		const artistPhotoComponent = (
			<ArtistPhoto
				name={name}
				imageUrl={imageUrl}
				size={size}
				priority={priority}
				className={className}
				rounded={rounded}
				aspectRatio={aspectRatio}
				isSurprise={isSurprise}
				isProgram={isProgram}
			/>
		);

		// If no href provided, just return the photo
		if (!href) {
			return <div className="flex-shrink-0">{artistPhotoComponent}</div>;
		}

		// Wrap in Link with standardized hover effects
		return (
			<div className="flex-shrink-0">
				<Link
					href={href}
					className={cn(
						"group block outline-none",
						rounded === "circle" ? "rounded-full" : "rounded-lg",
						"focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2",
					)}
					aria-label={ariaLabel || `View ${name} profile`}
					onClick={handleClick}
				>
					<div
						className={cn(
							"overflow-hidden transition-all duration-200",
							rounded === "circle" ? "rounded-full" : "rounded-lg",
							"group-hover:ring-4 group-hover:ring-primary/10",
							"group-active:scale-95 group-active:ring-primary/20",
						)}
					>
						<div className="transition-transform duration-200 group-hover:scale-105 group-active:scale-100">
							{artistPhotoComponent}
						</div>
					</div>
				</Link>
			</div>
		);
	},
);

ClickableArtistPhoto.displayName = "ClickableArtistPhoto";
